import { HttpClient } from "@angular/common/http";
import {
  Component,
  ElementRef,
  EventEmitter,
  Output,
  ViewChild,
} from "@angular/core";
import { NgSelectComponent } from "@ng-select/ng-select";
import { NotifierService } from "src/app/_services/notifier.service";
import { APIService } from "src/app/api/api.service";
import { LaunchEc2OperationsComponent } from "src/app/views/operations/launch-ec2/launch-ec2.operations.component";

@Component({
  selector: "app-ec2-launch-modal",
  templateUrl: "./launch-ec2-modal.component.html",
  styleUrls: [
    "../../../launch-ec2/launch-ec2.operations.component.css",
    "./launch-ec2-modal.component.css",
  ],
})
export class Ec2LaunchModalComponent extends LaunchEc2OperationsComponent {
  @ViewChild("ec2LaunchModalBody") ec2LaunchModalBody?: ElementRef<HTMLDivElement>;
  @ViewChild("securityGroupModalBody") securityGroupModalBody?: ElementRef<HTMLDivElement>;
  @Output() close = new EventEmitter<boolean>();

  wizardStep: number = 1;
  wizardSubmitAttempted: boolean = false;
  readonly operatingSystemDescriptions: Record<string, string> = {
    "Amazon Linux":
      "AWS-provided Linux distribution optimized for cloud workloads.",
    Debian:
      "Community-maintained Linux distribution known for stability and flexibility.",
    "Red Hat Enterprise Linux":
      "Enterprise Linux platform with commercial support and security features.",
    Ubuntu:
      "Popular Linux distribution with broad application and community support.",
    Windows:
      "Microsoft Windows Server operating system for Windows-based workloads.",
    SUSE:
      "Enterprise Linux platform designed for business-critical workloads.",
    "Oracle Linux":
      "Enterprise Linux distribution optimized for Oracle workloads and applications.",
  };
  readonly wizardStepLabels: string[] = [
    "OS",
    "Type",
    "Network",
    "Storage",
    "Tags",
    "Review",
  ];

  pricingLoading: boolean = false;
  savingRequest: boolean = false;
  pricingError: string = "";
  saveError: string = "";
  saveSuccessMessage: string = "";
  saveRequestId: string = "";
  pricing: any = null;

  constructor(
    private readonly modalApiServer: APIService,
    private readonly modalNotifier: NotifierService,
    modalHttp: HttpClient
  ) {
    super(modalApiServer, modalNotifier, modalHttp);
  }

  onLaunchModalDropdownOpen(select: NgSelectComponent, container: "request" | "securityGroup") {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.ensureLaunchModalDropdownVisible(select, container);
      });
    });
  }

  requestClose(saved: boolean = false) {
    if (this.savingRequest || this.loading.createSecurityGroup) {
      return;
    }
    this.close.emit(saved);
  }

  async nextWizardStep() {
    this.wizardSubmitAttempted = true;
    if (!this.isWizardStepValid(this.wizardStep)) {
      this.modalNotifier.alert(
        "Warning",
        "",
        this.getWizardStepValidationMessage(this.wizardStep),
        "warning",
        3500
      );
      return;
    }

    if (this.wizardStep < this.wizardStepLabels.length) {
      this.wizardStep += 1;
      this.wizardSubmitAttempted = false;
    }

    if (this.wizardStep === this.wizardStepLabels.length) {
      await this.loadPricingForReview();
    }
  }

  previousWizardStep() {
    if (this.wizardStep <= 1) {
      return;
    }
    this.wizardStep -= 1;
    this.wizardSubmitAttempted = false;
  }

  async refreshReview() {
    if (!this.isWizardStepValid(this.wizardStepLabels.length - 1)) {
      return;
    }
    await this.loadPricingForReview();
  }

  onOperatingSystemSelect(value: string) {
    if (this.form.operating_system === value) {
      return;
    }
    this.form.operating_system = value;
    this.loadAmis();
  }

  hasSelectedOperatingSystem(value: string): boolean {
    return this.form.operating_system === value;
  }

  getOperatingSystemDescription(value: string): string {
    return (
      this.operatingSystemDescriptions[value] || "Select this OS for AMI lookup."
    );
  }

  isWizardStepComplete(index: number): boolean {
    return this.wizardStep > index + 1;
  }

  isWizardStepActive(index: number): boolean {
    return this.wizardStep === index + 1;
  }

  private hasBasicLaunchDetails(): boolean {
    return !!(
      (this.form?.ec2_launch_justification ?? "").toString().trim() &&
      (this.form?.operating_system ?? "").toString().trim() &&
      (this.form?.ami_id ?? "").toString().trim() &&
      (this.form?.architecture ?? "").toString().trim()
    );
  }

  private hasInstanceTypeDetails(): boolean {
    return !!(
      (this.form?.instance_type ?? "").toString().trim() &&
      (this.form?.key_name ?? "").toString().trim() &&
      (this.form?.iam_instance_profile_arn ?? "").toString().trim() &&
      (this.form?.metadata_options?.http_tokens ?? "").toString().trim() &&
      this.form?.monitoring?.enabled !== null &&
      this.form?.monitoring?.enabled !== undefined &&
      (this.form?.placement?.tenancy ?? "").toString().trim() &&
      this.form?.ebs_optimized !== null &&
      this.form?.ebs_optimized !== undefined &&
      (this.form?.instance_initiated_shutdown_behavior ?? "")
        .toString()
        .trim()
    );
  }

  private hasNetworkDetails(): boolean {
    return !!(
      (this.form?.vpc_id ?? "").toString().trim() &&
      (this.form?.subnet_id ?? "").toString().trim() &&
      Array.isArray(this.form?.security_group_ids) &&
      this.form.security_group_ids.length > 0 &&
      this.form?.associate_public_ip_address !== null &&
      this.form?.associate_public_ip_address !== undefined
    );
  }

  private hasValidTagRows(): boolean {
    return !!(
      Array.isArray(this.form?.tags) &&
      this.form.tags.length > 0 &&
      this.form.tags.every(
        (tag: any) =>
          (tag?.key ?? "").toString().trim() &&
          (tag?.value ?? "").toString().trim()
      )
    );
  }

  private hasTagDetails(): boolean {
    return !!(
      (this.form?.instance_name ?? "").toString().trim() && this.hasValidTagRows()
    );
  }

  private isWizardStepValid(step: number): boolean {
    switch (step) {
      case 1:
        return this.hasBasicLaunchDetails();
      case 2:
        return this.hasInstanceTypeDetails();
      case 3:
        return this.hasNetworkDetails();
      case 4:
        return true;
      case 5:
        return this.hasTagDetails();
      case 6:
        return (
          this.hasBasicLaunchDetails() &&
          this.hasInstanceTypeDetails() &&
          this.hasNetworkDetails() &&
          this.hasTagDetails()
        );
      default:
        return false;
    }
  }

  private getWizardStepValidationMessage(step: number): string {
    switch (step) {
      case 1:
        return "Please complete the OS, AMI, architecture, and launch justification details.";
      case 2:
        return "Please complete all required instance type settings before continuing.";
      case 3:
        return "Please complete the VPC, subnet, public IP, and security group selection.";
      case 4:
        return "Please review the storage configuration before continuing.";
      case 5:
        return "Please add the instance name and complete all tag key/value pairs.";
      case 6:
        return "Please review the launch request details.";
      default:
        return "Please complete the required launch details.";
    }
  }

  private async postRequest(url: string, body: any) {
    try {
      const r = await this.modalApiServer.postDataPromis(url, body, this.headers());
      if (typeof r === "string") {
        return null;
      }
      return r;
    } catch {
      return null;
    }
  }

  private getApiErrorMessage(response: any): string {
    if (!response) {
      return "";
    }
    if (typeof response === "string") {
      return response;
    }

    if (Array.isArray(response.detail) && response.detail.length > 0) {
      return response.detail
        .map((item: any) => item?.msg || item?.message || JSON.stringify(item))
        .join(", ");
    }

    if (Array.isArray(response.errors) && response.errors.length > 0) {
      return response.errors
        .map((item: any) => item?.message || item?.msg || item)
        .join(", ");
    }

    return (
      response.error ||
      response.error_message ||
      response.message ||
      response.msg ||
      ""
    ).toString();
  }

  async loadPricingForReview() {
    this.pricing = null;
    this.pricingError = "";

    if (
      !(this.form?.instance_type ?? "").toString().trim() ||
      !(this.form?.operating_system ?? "").toString().trim() ||
      !(this.regionId ?? "").toString().trim()
    ) {
      this.pricingError =
        "Instance type, operating system, and region are required to fetch pricing.";
      return;
    }

    this.pricingLoading = true;
    const url = `${this.apiBaseUrl}/admin/apis/create_ec2_instance/api/ec2/pricing`;
    const payload = {
      instance_type: this.form.instance_type,
      region: this.regionId,
      operating_system: this.form.operating_system,
    };

    const response = await this.postRequest(url, payload);
    const apiError = this.getApiErrorMessage(response);
    if (apiError) {
      this.pricingError = apiError;
      this.pricingLoading = false;
      return;
    }

    if (
      response &&
      response.hourly_cost_usd !== undefined &&
      response.monthly_cost_usd !== undefined &&
      response.currency
    ) {
      this.pricing = response;
    } else {
      this.pricingError = "Unable to fetch pricing right now.";
    }
    this.pricingLoading = false;
  }

  async launchRequest() {
    this.saveError = "";
    this.saveSuccessMessage = "";
    this.saveRequestId = "";
    this.wizardSubmitAttempted = true;

    if (!this.isWizardStepValid(this.wizardStepLabels.length)) {
      this.modalNotifier.alert(
        "Warning",
        "",
        "Please complete all required launch fields before submitting.",
        "warning",
        3500
      );
      return;
    }

    if (!this.pricing && !this.pricingLoading) {
      await this.loadPricingForReview();
    }

    if (!this.pricing || this.pricingError) {
      this.saveError =
        this.pricingError ||
        "Pricing must be available before the request can be submitted.";
      return;
    }

    this.savingRequest = true;
    const url = `${this.apiBaseUrl}/admin/apis/create_ec2_instance/api/ec2/save-request`;
    const response = await this.postRequest(url, this.buildSaveRequestBody());

    if (response?.request_id || response?.execution_arn || response?.accountid_region) {
      this.saveRequestId = response?.request_id || "";
      this.saveSuccessMessage = response?.message || "Request saved successfully.";
      this.modalNotifier.alert(
        "Success",
        "",
        this.saveSuccessMessage,
        "success",
        4000
      );
      this.savingRequest = false;
      return;
    }

    const apiError = this.getApiErrorMessage(response);
    this.saveError = apiError || "Save request failed. Please try again.";
    this.savingRequest = false;
  }

  getSelectedAmiOption() {
    return (
      (this.options?.amis || []).find((item: any) => item?.ami_id === this.form?.ami_id) || null
    );
  }

  getSelectedInstanceTypeOption() {
    return (
      (this.options?.instanceTypes || []).find(
        (item: any) => item?.instance_type === this.form?.instance_type
      ) || null
    );
  }

  getSelectedVpcOption() {
    return (
      (this.options?.vpcs || []).find((item: any) => item?.vpc_id === this.form?.vpc_id) || null
    );
  }

  getSelectedSubnetOption() {
    return (
      (this.options?.subnets || []).find((item: any) => item?.subnet_id === this.form?.subnet_id) ||
      null
    );
  }

  getSelectedSecurityGroupOptions() {
    const selectedIds = Array.isArray(this.form?.security_group_ids)
      ? this.form.security_group_ids
      : [];
    return (this.options?.securityGroups || []).filter((item: any) =>
      selectedIds.includes(
        item?.security_group_id ?? item?.group_id ?? item?.sg_id ?? item?.id
      )
    );
  }

  getSecurityGroupDisplayName(option: any): string {
    return (
      option?.security_group_name ||
      option?.group_name ||
      option?.sg_name ||
      option?.name ||
      option?.security_group_id ||
      option?.group_id ||
      "-"
    );
  }

  getSecurityGroupDisplayId(option: any): string {
    return (
      option?.security_group_id || option?.group_id || option?.sg_id || option?.id || "-"
    );
  }

  getSubnetDisplayLabel(): string {
    const subnet = this.getSelectedSubnetOption();
    if (!subnet) {
      return this.form?.subnet_id || "-";
    }

    const subnetName = (subnet?.subnet_name || "").toString().trim();
    const az = (subnet?.availability_zone || subnet?.az || "").toString().trim();
    const parts = [subnetName, subnet?.subnet_id, az].filter((value: any) =>
      (value || "").toString().trim()
    );
    return parts.join(" - ") || subnet?.subnet_id || "-";
  }

  getVolumeSummary(): string {
    const mapping = this.form?.block_device_mappings?.[0];
    const size = mapping?.ebs?.volume_size;
    const type = (mapping?.ebs?.volume_type || "").toString().trim();
    const encrypted =
      mapping?.ebs?.delete_on_termination === null ||
      mapping?.ebs?.delete_on_termination === undefined
        ? ""
        : mapping?.ebs?.delete_on_termination
        ? "delete on termination"
        : "retain on termination";
    return [size ? `${size} GB` : "", type, encrypted]
      .filter((value: string) => !!value)
      .join(" - ") || "-";
  }

  hasAnyUserData(): boolean {
    return !!(this.form?.user_data ?? "").toString().trim();
  }

  private ensureLaunchModalDropdownVisible(
    select: NgSelectComponent,
    container: "request" | "securityGroup"
  ) {
    const scrollContainer = this.getLaunchModalScrollContainer(container);
    const triggerElement = select?.element as HTMLElement | null;
    const panelElement = this.getLaunchModalDropdownPanelElement(select);

    if (!scrollContainer || !triggerElement || !panelElement) {
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const triggerRect = triggerElement.getBoundingClientRect();
    const panelRect = panelElement.getBoundingClientRect();
    const verticalPadding = 12;
    const visibleTop = containerRect.top + verticalPadding;
    const visibleBottom = containerRect.bottom - verticalPadding;
    const contentTop = Math.min(triggerRect.top, panelRect.top);
    const contentBottom = Math.max(triggerRect.bottom, panelRect.bottom);

    const isFullyVisible =
      triggerRect.top >= visibleTop &&
      triggerRect.bottom <= visibleBottom &&
      panelRect.top >= visibleTop &&
      panelRect.bottom <= visibleBottom;

    if (isFullyVisible) {
      return;
    }

    let delta = 0;
    if (contentTop < visibleTop) {
      delta = contentTop - visibleTop;
    } else if (contentBottom > visibleBottom) {
      delta = contentBottom - visibleBottom;
    }

    if (Math.abs(delta) < 1) {
      return;
    }

    const nextScrollTop = this.clampLaunchModalValue(
      scrollContainer.scrollTop + delta,
      0,
      scrollContainer.scrollHeight - scrollContainer.clientHeight
    );

    if (Math.abs(nextScrollTop - scrollContainer.scrollTop) < 1) {
      return;
    }

    scrollContainer.scrollTo({
      top: nextScrollTop,
      behavior: "smooth",
    });

    window.setTimeout(() => {
      select.dropdownPanel?.adjustPosition();
    }, 260);
  }

  private getLaunchModalScrollContainer(container: "request" | "securityGroup") {
    return container === "securityGroup"
      ? this.securityGroupModalBody?.nativeElement || null
      : this.ec2LaunchModalBody?.nativeElement || null;
  }

  private getLaunchModalDropdownPanelElement(select: NgSelectComponent): HTMLElement | null {
    const scrollElement = select?.dropdownPanel?.scrollElementRef?.nativeElement as HTMLElement | undefined;
    return (scrollElement?.closest("ng-dropdown-panel") as HTMLElement | null) || scrollElement || null;
  }

  private clampLaunchModalValue(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
  }
}
